/**
 * Keep the recipient-link segment out of everything the client exposes
 * (vingreact#717, client slice 1123b of vingapi#1123, contract 5).
 *
 * The consume path `/view/<stub>/<link>/` carries a per-recipient token in its
 * second segment: whoever holds the URL is identified as that person on every
 * open. It rides in the PATH (and, since 1123b, as `?link=` on every consume
 * request), so it must never reach a header, a crash report, a breadcrumb or a
 * copy-pasteable error report.
 *
 * `stripLinkSegment` rewrites, in arbitrary text (a path, an href, a stack, a
 * serialised report):
 *   - `/view/<stub>/<link>`        → `/view/<stub>/<redacted>` (a path that
 *     STARTS with `/view/`: string start, after a scheme+host, or after a
 *     non-path character such as whitespace, a quote or `=`; `/api/view/…`
 *     request paths are left alone),
 *   - `%2Fview%2F<stub>%2F<link>`  → `%2Fview%2F<stub>%2F<redacted>` (the same
 *     path percent-encoded into a `redirect=` param on the sign-in page),
 *   - `?link=<value>` / `&link=<value>` → `?link=<redacted>` (a consume request
 *     URL, e.g. a keepalive PUT's URL quoted in an error message).
 *
 * It does NOT touch `?claim=` and the other URL secrets: that is
 * `scrubUrlSecrets`' job. Sinks apply both. Pure and idempotent.
 */

// Decoded path form. A candidate `/view/<stub>/<link>` is rewritten only when
// `/view/` STARTS a path, so `/api/view/shares/<stub>/` (a request path whose
// segments are not a recipient link) is never rewritten. That anchor used to
// be a lookbehind, which Safari < 16.4 cannot parse (a SyntaxError on the
// whole entry chunk: vingreact#981), so the prefix is checked by hand in
// `redactViewPaths` against the ORIGINAL string. A capture-group prefix
// (`(^|[^…])(\/view\/…)`) would consume the character before the path and
// skip the second of two adjacent links, so it is not an equivalent rewrite.
const VIEW_PATH_RE = /(\/view\/[^/?#\s"'<>]+\/)(?!<redacted>)([^/?#\s"'<>]+)/g;
// A character that, directly before `/view/`, means `/view/` starts a path.
const PATH_START_CHAR_RE = /[^A-Za-z0-9._~%/-]/;
// Text ending in a scheme separator and host: `/view/` right after it is a path start.
const AFTER_SCHEME_HOST_RE = /:\/\/[^/\s?#]*$/;

// Percent-encoded path form (a `redirect=` value). Stub and link are
// URL-safe, so neither contains an escape. The prefix character is captured
// and re-emitted (no lookbehind, #981); a match ends in `[A-Za-z0-9_-]`,
// which is never a qualifying prefix, so consuming it skips no adjacent match.
const ENCODED_VIEW_PATH_RE =
  /(^|[^A-Za-z0-9._~%-])(%2Fview%2F[A-Za-z0-9_-]+%2F)(?!<redacted>)([A-Za-z0-9_-]+)/gi;

// `link` as a query param (a consume request URL).
const LINK_PARAM_RE = /([?&#])(link=)(?!<redacted>)([^&#\s'"]+)/g;

function startsPath(input: string, index: number): boolean {
  if (index === 0) return true;
  if (PATH_START_CHAR_RE.test(input.charAt(index - 1))) return true;
  return AFTER_SCHEME_HOST_RE.test(input.slice(0, index));
}

function redactViewPaths(input: string): string {
  VIEW_PATH_RE.lastIndex = 0;
  let out = '';
  let copiedTo = 0;
  let match: RegExpExecArray | null;
  while ((match = VIEW_PATH_RE.exec(input)) !== null) {
    if (!startsPath(input, match.index)) {
      VIEW_PATH_RE.lastIndex = match.index + 1;
      continue;
    }
    out += input.slice(copiedTo, match.index) + match[1] + '<redacted>';
    copiedTo = VIEW_PATH_RE.lastIndex;
  }
  return out + input.slice(copiedTo);
}

export function stripLinkSegment(pathOrHref: string): string {
  ENCODED_VIEW_PATH_RE.lastIndex = 0;
  LINK_PARAM_RE.lastIndex = 0;
  return redactViewPaths(pathOrHref)
    .replace(ENCODED_VIEW_PATH_RE, '$1$2<redacted>')
    .replace(LINK_PARAM_RE, '$1$2<redacted>');
}

/**
 * `stripLinkSegment` over every string leaf of a value (report `context`,
 * breadcrumb `data`). Non-string leaves pass through; the input is not mutated.
 */
export function stripLinkSegmentDeep<T>(value: T): T {
  if (typeof value === 'string') return stripLinkSegment(value) as T;
  if (Array.isArray(value)) return value.map((item: unknown) => stripLinkSegmentDeep(item)) as T;
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = stripLinkSegmentDeep(v);
    return out as T;
  }
  return value;
}
